import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bookingAPI, inventoryAPI, invoiceAPI, alertAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';

const STATUS_BADGE = {
  pending:    'badge-amber',
  confirmed:  'badge-blue',
  'in-progress': 'badge-cyan',
  completed:  'badge-green',
  cancelled:  'badge-red',
};

const SEVERITY_BADGE = {
  info:     'badge-blue',
  warning:  'badge-amber',
  critical: 'badge-red',
};

export default function Dashboard() {
  const { isAdmin, isStaff } = useAuth();
  const [stats, setStats] = useState({ bookings: 0, revenue: 0, vehicles: 0, lowStock: 0 });
  const [recentBookings, setRecentBookings] = useState([]);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [bookRes, alertRes] = await Promise.all([
          bookingAPI.getAll({ limit: 5 }),
          alertAPI.getAll({ isResolved: false, limit: 5 }),
        ]);
        setRecentBookings(bookRes.data.data || []);
        setRecentAlerts(alertRes.data.data || []);

        if (isStaff) {
          const [bStats, invStats, invRes] = await Promise.all([
            bookingAPI.getStats(),
            inventoryAPI.getStats(),
            invoiceAPI.getStats(),
          ]);
          setStats({
            bookings: bStats.data.data?.total || 0,
            revenue: invRes.data.data?.totalRevenue || 0,
            lowStock: invStats.data.data?.lowStockCount || 0,
            vehicles: bStats.data.data?.totalVehicles || 0,
          });

          // Build weekly chart data from booking stats
          const weekly = bStats.data.data?.weekly || [];
          setChartData(weekly.length ? weekly : [
            { day: 'Mon', bookings: 4 }, { day: 'Tue', bookings: 7 },
            { day: 'Wed', bookings: 5 }, { day: 'Thu', bookings: 9 },
            { day: 'Fri', bookings: 12 }, { day: 'Sat', bookings: 8 },
            { day: 'Sun', bookings: 3 },
          ]);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isStaff]);

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  if (loading) return <div className="loading-screen" style={{ height: 400 }}><div className="spinner" /></div>;

  return (
    <div>
      {/* ── STAT CARDS ── */}
      {isStaff && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon blue">📅</div>
            <div>
              <div className="stat-value">{stats.bookings}</div>
              <div className="stat-label">Total Bookings</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green">💰</div>
            <div>
              <div className="stat-value">{fmt(stats.revenue)}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon amber">📦</div>
            <div>
              <div className="stat-value">{stats.lowStock}</div>
              <div className="stat-label">Low Stock Items</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon cyan">🚗</div>
            <div>
              <div className="stat-value">{stats.vehicles}</div>
              <div className="stat-label">Registered Vehicles</div>
            </div>
          </div>
        </div>
      )}

      {/* ── CHARTS + ALERTS ROW ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isStaff ? '1fr 380px' : '1fr', gap: 20, marginBottom: 24 }}>
        {/* Weekly Bookings Chart */}
        {isStaff && (
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Weekly Bookings</div>
                <div className="card-subtitle">Bookings per day this week</div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }}
                  cursor={{ fill: 'var(--accent-light)' }}
                />
                <Bar dataKey="bookings" fill="var(--accent)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Recent Alerts */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Active Alerts</div>
              <div className="card-subtitle">Unresolved notifications</div>
            </div>
            <Link to="/alerts" className="btn btn-ghost btn-sm">View all</Link>
          </div>
          {recentAlerts.length === 0 ? (
            <div className="empty-state" style={{ padding: '30px 0' }}>
              <div className="empty-state-icon">✅</div>
              <div className="empty-state-title">All clear!</div>
              <div className="empty-state-text">No active alerts right now.</div>
            </div>
          ) : (
            recentAlerts.map(alert => (
              <div key={alert._id} className={`alert-item ${alert.isRead ? '' : 'unread'} ${alert.severity}`}>
                <div className={`alert-dot ${alert.severity}`} />
                <div className="alert-content">
                  <div className="alert-title">{alert.title}</div>
                  <div className="alert-message" style={{ fontSize: 12 }}>{alert.message.slice(0, 80)}…</div>
                  <div className="alert-time">{fmtDate(alert.createdAt)}</div>
                </div>
                <span className={`badge ${SEVERITY_BADGE[alert.severity]}`}>{alert.severity}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── RECENT BOOKINGS TABLE ── */}
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Recent Bookings</div>
            <div className="card-subtitle">Latest service appointments</div>
          </div>
          <Link to="/bookings" className="btn btn-primary btn-sm">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Booking
          </Link>
        </div>

        {recentBookings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📅</div>
            <div className="empty-state-title">No bookings yet</div>
            <div className="empty-state-text">Create your first service booking to get started.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Booking #</th>
                  <th>Customer</th>
                  <th>Vehicle</th>
                  <th>Service</th>
                  <th>Date</th>
                  <th>Slot</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map(b => (
                  <tr key={b._id}>
                    <td style={{ fontWeight: 600, color: 'var(--accent)' }}>{b.bookingNumber}</td>
                    <td>{b.customer?.name || '—'}</td>
                    <td>{b.vehicle ? `${b.vehicle.make} ${b.vehicle.model}` : '—'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{b.serviceType?.replace(/-/g, ' ')}</td>
                    <td>{fmtDate(b.serviceDate)}</td>
                    <td>{b.timeSlot}</td>
                    <td><span className={`badge ${STATUS_BADGE[b.status] || 'badge-gray'}`}>{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}